import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, ListChecks, Gavel, Briefcase, Users, FolderKanban,
  FileText, ClipboardList, Inbox, BarChart3, Settings, UserCircle,
  LogOut, Menu, Search, Bell, GraduationCap, Wallet, Receipt, BadgeDollarSign,
  Settings2, ChevronDown, ChevronLeft, ChevronRight, Wand2, FolderOpen,
} from "lucide-react";
import { useAuthStore } from "../../store/useAuthStore";
import { useChatStore } from "../../store/useChatStore";
import { roleLabel, roleBadgeClass } from "../../lib/role";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";

// Grouped nav (reference layout): a few top-level links, an "Operations"
// expandable group, then the rest. A group has `children` instead of `path`.
type NavLeaf = { label: string; icon: any; path: string };
type NavNode = NavLeaf | { label: string; icon: any; children: NavLeaf[] };

const NAV_MAIN: NavNode[] = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/bd/dashboard" },
  { label: "Inbox", icon: Inbox, path: "/chat" },
  { label: "Contacts", icon: Users, path: "/clients" },
  { label: "Upwork Jobs", icon: Briefcase, path: "/upwork-jobs" },
  {
    label: "Operations",
    icon: Settings2,
    children: [
      { label: "Bidding", icon: Gavel, path: "/bids" },
      { label: "Proposals", icon: FileText, path: "/proposals" },
      { label: "Questionnaires", icon: ClipboardList, path: "/questionnaires" },
      { label: "Projects", icon: FolderKanban, path: "/projects" },
      { label: "Activities", icon: ListChecks, path: "/activities" },
    ],
  },
  {
    label: "Finance",
    icon: Wallet,
    children: [
      { label: "Invoices", icon: Receipt, path: "/bd/invoices" },
      { label: "Commissions", icon: BadgeDollarSign, path: "/bd/commissions" },
    ],
  },
  { label: "Interview Prep", icon: GraduationCap, path: "/interview-prep" },
  { label: "Files", icon: FolderOpen, path: "/files" },
  { label: "Analytics", icon: BarChart3, path: "/reports/daily" },
];

const isGroup = (n: NavNode): n is { label: string; icon: any; children: NavLeaf[] } =>
  (n as any).children !== undefined;

const NAV_BOTTOM = [
  { label: "Settings", icon: Settings, path: "/settings/integrations" },
  { label: "Account", icon: UserCircle, path: "/profile" },
];

// Route → page title shown in the top bar.
const PAGE_TITLES: { prefix: string; title: string }[] = [
  { prefix: "/bd/dashboard", title: "Dashboard" },
  { prefix: "/activities", title: "Activities" },
  { prefix: "/bids", title: "Bidding" },
  { prefix: "/upwork-jobs", title: "Upwork Jobs" },
  { prefix: "/clients", title: "Contacts" },
  { prefix: "/projects", title: "Projects" },
  { prefix: "/proposal-builder", title: "Proposal Builder" },
  { prefix: "/proposals", title: "Proposals" },
  { prefix: "/questionnaires", title: "Questionnaires" },
  { prefix: "/bd/invoices", title: "Invoices" },
  { prefix: "/bd/commissions", title: "Commissions" },
  { prefix: "/interview-prep", title: "Interview Prep" },
  { prefix: "/bd/files", title: "Files" },
  { prefix: "/files", title: "Files" },
  { prefix: "/chat", title: "Inbox" },
  { prefix: "/reports/daily", title: "Analytics" },
  { prefix: "/settings", title: "Settings" },
  { prefix: "/profile", title: "Account" },
];
const titleFor = (path: string) =>
  PAGE_TITLES.find((p) => path.startsWith(p.prefix))?.title || "Workspace";

const initials = (name?: string) =>
  (name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

export default function BDWorkspaceLayout() {
  const { authUser, logout }: any = useAuthStore();
  const { users: chatUsers, getUsers: getChatUsers, subscribeSidebar, unsubscribeSidebar } = useChatStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { collapsed, toggle } = useSidebarCollapsed("cre-bd-sidebar");
  // Default: only "Operations" is expanded; all other groups start collapsed.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const pageTitle = titleFor(location.pathname);

  // Keep an app-wide unread total for the Conversation badge.
  useEffect(() => {
    getChatUsers();
    subscribeSidebar();
    return () => unsubscribeSidebar();
  }, [getChatUsers, subscribeSidebar, unsubscribeSidebar]);

  const totalUnread = chatUsers.reduce((s, u) => s + (u.unreadCount || 0), 0);

  const NavItem = ({ item, badge, mini }: { item: { label: string; icon: any; path: string }; badge?: number; mini?: boolean }) => {
    const Icon = item.icon;
    return (
      <NavLink
        to={item.path}
        onClick={() => setMobileOpen(false)}
        title={mini ? item.label : undefined}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg py-2 text-sm font-medium transition-colors ${mini ? "justify-center px-0" : "px-3"} ${
            isActive
              ? "bg-primary text-white"
              : "text-on-surface-variant hover:bg-surface-container-highest"
          }`
        }
      >
        <Icon size={20} className="shrink-0" />
        {!mini && <span className="flex-1">{item.label}</span>}
        {!mini && badge ? (
          <span className="min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-red-500 text-white text-[11px] font-bold leading-none">
            {badge > 99 ? "99+" : badge}
          </span>
        ) : null}
        {mini && badge ? <span className="absolute ml-4 -mt-4 w-2 h-2 rounded-full bg-red-500" /> : null}
      </NavLink>
    );
  };

  const renderSide = (mini: boolean) => (
    <>
      <div className={`py-6 border-b border-outline-variant ${mini ? "px-2" : "px-5"}`}>
        <div className={`flex items-center gap-2.5 ${mini ? "justify-center" : ""}`}>
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-[#0b5fd6] grid place-items-center shrink-0">
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
              <circle cx="9" cy="12" r="5.5" stroke="white" strokeWidth="2" opacity="0.7" />
              <circle cx="15" cy="12" r="5.5" stroke="white" strokeWidth="2" />
            </svg>
          </div>
          {!mini && (
            <div className="min-w-0">
              <h1 className="text-lg font-black text-on-surface leading-tight">Omni Workspace</h1>
              <p className="text-[11px] font-semibold text-on-surface-variant truncate">by DymaxTech</p>
            </div>
          )}
        </div>
      </div>

      <div className={`flex flex-col gap-1 flex-grow overflow-y-auto overflow-x-hidden cre-scroll ${mini ? "p-2" : "p-3"}`}>
        {NAV_MAIN.map((it) => {
          if (!isGroup(it)) {
            return <NavItem key={it.path} item={it} mini={mini} badge={it.path === "/chat" ? totalUnread : undefined} />;
          }
          // Collapsed sidebar: flatten group children to icon-only rows.
          if (mini) {
            return it.children.map((c) => <NavItem key={c.path} item={c} mini />);
          }
          const open = openGroups[it.label] ?? (it.label === "Operations");
          const GIcon = it.icon;
          return (
            <div key={it.label} className="flex flex-col gap-0.5">
              <button
                onClick={() => setOpenGroups((g) => ({ ...g, [it.label]: !open }))}
                className="flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-highest transition-colors"
              >
                <span className="flex items-center gap-3"><GIcon size={20} className="shrink-0" /> {it.label}</span>
                <ChevronDown size={16} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
              </button>
              {open && (
                <div className="flex flex-col gap-0.5 pl-4">
                  {it.children.map((c) => {
                    const CIcon = c.icon;
                    return (
                      <NavLink
                        key={c.path}
                        to={c.path}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                            isActive ? "bg-primary text-white" : "text-on-surface-variant hover:bg-surface-container-highest"
                          }`
                        }
                      >
                        <CIcon size={18} className="shrink-0" /> <span className="flex-1">{c.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className={`border-t border-outline-variant flex flex-col gap-1 ${mini ? "p-2" : "p-3"}`}>
        {NAV_BOTTOM.map((it) => <NavItem key={it.path} item={it} mini={mini} />)}
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          title={mini ? "Logout" : undefined}
          className={`flex items-center gap-3 rounded-lg py-2 text-sm font-medium text-on-surface-variant hover:bg-error-container hover:text-error transition-colors ${mini ? "justify-center px-0" : "px-3"}`}
        >
          <LogOut size={20} className="shrink-0" /> {!mini && "Logout"}
        </button>
      </div>
    </>
  );

  return (
    <div className="bd-workspace flex h-screen overflow-hidden bg-background text-on-surface">
      {/* Desktop sidenav (collapsible) */}
      <nav className={`fixed left-0 top-0 h-full flex-col z-50 bg-surface-container-low border-r border-outline-variant flex-shrink-0 hidden md:flex transition-[width] duration-200 ${collapsed ? "w-20" : "w-64"}`}>
        {renderSide(collapsed)}
        {/* Collapse toggle — vertically centered on the sidebar edge */}
        <button
          onClick={toggle}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-surface-container-lowest border border-outline-variant shadow-sm grid place-items-center text-on-surface-variant hover:bg-surface-container hover:text-primary z-50"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </nav>

      {/* Mobile drawer (always expanded) */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm md:hidden" onClick={() => setMobileOpen(false)} />
          <nav className="fixed left-0 top-0 h-full flex flex-col z-50 bg-surface-container-low border-r border-outline-variant w-64 md:hidden">
            {renderSide(false)}
          </nav>
        </>
      )}

      {/* Main */}
      <main className={`flex-1 flex flex-col h-screen overflow-y-auto bg-background min-w-0 transition-[margin] duration-200 ${collapsed ? "md:ml-20" : "md:ml-64"}`}>
        {/* Top nav */}
        <header className="bg-surface-container-lowest border-b border-outline-variant shadow-sm flex justify-between items-center px-5 w-full sticky top-0 z-40 h-16 shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="md:hidden p-2 -ml-2 text-on-surface-variant hover:bg-surface-container rounded-full">
              <Menu size={22} />
            </button>
            <span className="text-2xl font-bold text-on-surface">{pageTitle}</span>
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <div className="relative hidden sm:block">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-outline" />
              <input
                className="pl-10 pr-3 py-2 border border-outline-variant rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none w-56 lg:w-64 bg-surface-container-lowest text-on-surface placeholder-outline"
                placeholder="Search..." type="text"
              />
            </div>
            <button className="p-2 text-on-surface-variant hover:bg-surface-container transition-colors rounded-full">
              <Bell size={20} />
            </button>
            <button onClick={() => navigate("/settings/integrations")} className="p-2 text-on-surface-variant hover:bg-surface-container transition-colors rounded-full">
              <Settings size={20} />
            </button>
            <button
              onClick={() => navigate("/profile")}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-surface-container transition"
              title={authUser?.fullName}
            >
              <span className="w-9 h-9 rounded-full bg-primary text-white grid place-items-center font-bold text-xs overflow-hidden shrink-0">
                {authUser?.profilePic
                  ? <img src={authUser.profilePic} alt="" className="w-full h-full rounded-full object-cover" />
                  : initials(authUser?.fullName)}
              </span>
              <span className="hidden sm:flex flex-col items-start leading-tight text-left">
                <span className="text-[12px] font-bold text-on-surface truncate max-w-[130px]">
                  {authUser?.fullName || "Profile"}
                </span>
                <span className={`text-[10px] font-bold px-1.5 py-px rounded-full ring-1 w-fit ${roleBadgeClass(authUser?.role)}`}>
                  {roleLabel(authUser?.role)}
                </span>
              </span>
            </button>
          </div>
        </header>

        <Outlet />
      </main>
    </div>
  );
}
